<template>
  <main :class="cn('min-md:max-w-large min-md:justify-center mx-auto mt-16 flex flex-col', props.class)">
    <UiTabs
      v-model:model-value="selectedServiceId"
      :list="staticProducts"
      :variants="{ default: 'ghost', active: 'default' }"
      :swiper="{
        enabled: isMobile,
      }"
      :classes="{
        root: 'gap-2',
      }"
    />

    <div class="mt-4 flex h-full flex-col gap-5">
      <PensionPortfolioValuePropositionTable :structure="selectedCategoryTable?.table" :isLoading />

      <PensionPortfolioValuePropositionCashbackInfoBanner class="mb-8 rounded-xl" />
    </div>
  </main>
</template>

<script setup lang="tsx">
const { isMobile } = useDisplay()

const props = withDefaults(
  defineProps<{
    class?: ClassValue
    isLocked?: boolean
    noRecommendationsFlow?: boolean
  }>(),
  { isLocked: true, noRecommendationsFlow: false }
)

const PortfolioProductTypes = {
  Pension: "pension-savings",
  LifeInsurance: "life-insurance",
  disabilityInsurance: "work-disability",
} as const

const staticProducts = [
  {
    value: PortfolioProductTypes.Pension,
    label: "חיסכון פנסיוני",
  },
  {
    value: PortfolioProductTypes.LifeInsurance,
    label: "כיסוי ביטוח חיים",
  },
  {
    value: PortfolioProductTypes.disabilityInsurance,
    label: "אובדן כושר עבודה",
  },
]

const selectedServiceId = ref(staticProducts[0].value)

const { data: tableData, isLoading } = useGetMyValuePropositionTableData(selectedServiceId)

const selectedCategoryTable = computed(() => {
  if (isLoading.value) return skeletonData()

  switch (selectedServiceId.value) {
    case PortfolioProductTypes.Pension:
      return pensionSavingsTable(tableData.value)

    case PortfolioProductTypes.LifeInsurance:
      return lifeInsuranceTable(tableData.value)

    case PortfolioProductTypes.disabilityInsurance:
      return disabilityInsuranceTable(tableData.value)

    default:
      return pensionSavingsTable(tableData.value)
  }
})

const pensionSavingsTable = (data: ValuePropositionTableData | undefined) => {
  if (!data?.pensionSavings) return undefined

  const { lumpSum, monthlyAllowance } = data.pensionSavings

  return {
    table: [
      {
        header: "סכום חד-פעמי",
        icon: "portfolio/coins-stacked",
        values: [
          {
            name: "סכום חד-פעמי שצברת עד היום",
            value: toCash(lumpSum.current, NIS),
            infoPopup: {
              title: "עלות מצטברת עד תום ביטוח",
              description:
                "עלות הביטוחים עולה עם הגיל, ולפיכך העלות המצטברת גדולה יותר ממכפלת העלות החודשית, במספר החודשים שנותרו",
            },
          },
          {
            name: "סכום חד-פעמי שתקבל בפרישה",
            value: toCash(lumpSum.forecast, NIS),
            infoPopup: {
              title: "עלות מצטברת עד תום ביטוח",
              description:
                "עלות הביטוחים עולה עם הגיל, ולפיכך העלות המצטברת גדולה יותר ממכפלת העלות החודשית, במספר החודשים שנותרו",
            },
          },
        ],
      },
      {
        header: "קצבה חודשית",
        icon: "portfolio/coins",
        values: [
          {
            name: "סכום קצבה שצברת עד היום",
            value: toCash(monthlyAllowance.current, NIS),
            infoPopup: {
              title: "עלות מצטברת עד תום ביטוח",
              description:
                "עלות הביטוחים עולה עם הגיל, ולפיכך העלות המצטברת גדולה יותר ממכפלת העלות החודשית, במספר החודשים שנותרו",
            },
          },
          {
            name: "קצבה חודשית שתקבל בפרישה",
            value: toCash(monthlyAllowance.forecast, NIS),
            infoPopup: {
              title: "עלות מצטברת עד תום ביטוח",
              description:
                "עלות הביטוחים עולה עם הגיל, ולפיכך העלות המצטברת גדולה יותר ממכפלת העלות החודשית, במספר החודשים שנותרו",
            },
          },
        ],
      },
    ],
  }
}

const lifeInsuranceTable = (data: ValuePropositionTableData | undefined) => {
  if (!data?.lifeInsurance) return undefined

  const { deathBenefit, survivorsAllowance } = data.lifeInsurance

  return {
    table: [
      {
        header: "ביטוח חיים",
        icon: "portfolio/coins-stacked",
        values: [
          {
            name: "עלות חודשית לביטוח חיים",
            value: toCash(deathBenefit.monthlyAllowance, NIS),
            infoPopup: {
              title: "עלות מצטברת עד תום ביטוח",
              description:
                "עלות הביטוחים עולה עם הגיל, ולפיכך העלות המצטברת גדולה יותר ממכפלת העלות החודשית, במספר החודשים שנותרו",
            },
          },
          {
            name: "סכום ביטוח חיים",
            value: toCash(deathBenefit.lumpSum, NIS),
            infoPopup: {
              title: "עלות מצטברת עד תום ביטוח",
              description:
                "עלות הביטוחים עולה עם הגיל, ולפיכך העלות המצטברת גדולה יותר ממכפלת העלות החודשית, במספר החודשים שנותרו",
            },
          },
        ],
      },
      {
        header: "קצבת שארים",
        icon: "portfolio/coins",
        values: [
          {
            name: "עלות לקצבת שארים",
            value: toCash(survivorsAllowance.monthlyAllowance, NIS),
            infoPopup: {
              title: "עלות מצטברת עד תום ביטוח",
              description:
                "עלות הביטוחים עולה עם הגיל, ולפיכך העלות המצטברת גדולה יותר ממכפלת העלות החודשית, במספר החודשים שנותרו",
            },
          },
          {
            name: "סכום קצבת שארים",
            value: toCash(survivorsAllowance.lumpSum, NIS),
            infoPopup: {
              title: "עלות מצטברת עד תום ביטוח",
              description:
                "עלות הביטוחים עולה עם הגיל, ולפיכך העלות המצטברת גדולה יותר ממכפלת העלות החודשית, במספר החודשים שנותרו",
            },
          },
        ],
      },
    ],
  }
}

const disabilityInsuranceTable = (data: ValuePropositionTableData | undefined) => {
  if (!data?.disabilityInsurance) return undefined

  const { monthlyCost, monthlyAllowance } = data.disabilityInsurance

  return {
    table: [
      {
        header: "אובדן כושר עבודה",
        icon: "portfolio/coins-stacked",
        values: [
          {
            name: "עלות חודשית לאובדן כושר עבודה",
            value: toCash(monthlyCost, NIS),
            infoPopup: {
              title: "עלות מצטברת עד תום ביטוח",
              description:
                "עלות הביטוחים עולה עם הגיל, ולפיכך העלות המצטברת גדולה יותר ממכפלת העלות החודשית, במספר החודשים שנותרו",
            },
          },
          {
            name: "סכום קצבה חודשית",
            value: toCash(monthlyAllowance, NIS),
            infoPopup: {
              title: "עלות מצטברת עד תום ביטוח",
              description:
                "עלות הביטוחים עולה עם הגיל, ולפיכך העלות המצטברת גדולה יותר ממכפלת העלות החודשית, במספר החודשים שנותרו",
            },
          },
        ],
      },
    ],
  }
}

const skeletonData = () => {
  return {
    table: [
      {
        header: "lorem",
        icon: "portfolio/coins-stacked",
        values: [
          { name: "lorem", value: "lorem" },
          { name: "lorem", value: "lorem" },
        ],
      },
      {
        header: "lorem",
        icon: "portfolio/coins",
        values: [
          { name: "lorem", value: "lorem" },
          { name: "lorem", value: "lorem" },
        ],
      },
    ],
  }
}
</script>
